import { useQuery } from '@tanstack/react-query';
import { api, type Me } from './api';

export type AuthState =
  | { state: 'loading' }
  | { state: 'guest' }
  | { state: 'signed-in'; me: Me };

/** Pure mapping from the /me query's flags to an auth state, split out
 *  so the (subtle) rules are unit-testable without a live query.
 *
 *  Only the FIRST load is `loading` — background refetches must NOT
 *  blank the app, or every stale-session revalidation would flash the
 *  full-screen "Loading…" (and a public magic-link landing would hide
 *  behind it). Any settled error (401, network, 5xx, the api-layer 6s
 *  abort) falls through to `guest` so the user lands on sign-in. */
export function deriveAuthState(q: {
  isLoading: boolean;
  error: unknown;
  data: Me | undefined;
}): AuthState {
  if (q.isLoading) return { state: 'loading' };
  if (q.error || !q.data) return { state: 'guest' };
  return { state: 'signed-in', me: q.data };
}

export function useAuth(): AuthState {
  const q = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/me'),
    retry: false,
    staleTime: 60_000,
    gcTime: 60_000,
  });

  // `isLoading` (react-query v5) is true only on the initial fetch with
  // no cached data — exactly the "we don't know yet" window. The api
  // layer aborts /me after 6s, so this can't wedge indefinitely.
  return deriveAuthState({ isLoading: q.isLoading, error: q.error, data: q.data });
}
